import { Container } from 'pixi.js'
import {
  BASE_ZOOM,
  ZOOM_FACTOR,
  MIN_ZOOM,
  MAX_ZOOM,
  FOLLOW_LERP,
  snakeCameraScaleFromLength,
} from '@star-snake/shared'
import { getAppViewport } from '../ui/appShell'

export class Camera {
  private container: Container
  private targetX: number = 0
  private targetZ: number = 0
  private currentX: number = 0
  private currentZ: number = 0
  private zoom: number = BASE_ZOOM
  private targetZoom: number = BASE_ZOOM
  private zoomScale: number = 1
  private screenWidth: number = 0
  private screenHeight: number = 0
  private followLerp: number = FOLLOW_LERP

  // Camera shake state
  private shakeOffsetX: number = 0
  private shakeOffsetZ: number = 0
  private shakeIntensity: number = 0
  private shakeDuration: number = 0
  private shakeElapsed: number = 0

  constructor(container: Container) {
    this.container = container
    this.updateScreenSize()
  }

  updateScreenSize(): void {
    const viewport = getAppViewport()
    this.screenWidth = viewport.width
    this.screenHeight = viewport.height
  }

  setTarget(x: number, z: number): void {
    this.targetX = x
    this.targetZ = z
  }

  setFollowLerp(value: number): void {
    if (!Number.isFinite(value)) return
    this.followLerp = Math.max(0, Math.min(1, value))
  }

  setZoomForLength(snakeLength: number): void {
    const scale = snakeCameraScaleFromLength(snakeLength)
    const scaledBaseZoom = (BASE_ZOOM * this.zoomScale) / scale
    const scaledMinZoom = (MIN_ZOOM * this.zoomScale) / scale
    const scaledMaxZoom = (MAX_ZOOM * this.zoomScale) / scale
    this.targetZoom = Math.max(
      0.02,
      Math.min(0.35, Math.max(
        scaledMinZoom,
        Math.min(scaledMaxZoom, scaledBaseZoom - ZOOM_FACTOR * snakeLength),
      ))
    )
  }

  setZoomScale(scale: number): void {
    if (!Number.isFinite(scale)) return
    this.zoomScale = Math.max(0.8, Math.min(1.5, scale))
  }

  /**
   * Trigger camera shake with pixel-snapped random offsets and linear decay.
   * @param intensity Maximum shake amplitude in pixels
   * @param duration Shake duration in seconds
   */
  shake(intensity: number, duration: number): void {
    this.shakeIntensity = intensity
    this.shakeDuration = duration
    this.shakeElapsed = 0
  }

  update(dt: number): void {
    const viewport = getAppViewport()
    if (viewport.width !== this.screenWidth || viewport.height !== this.screenHeight) {
      this.updateScreenSize()
    }

    // Smooth lerp toward target
    const lerpFactor = this.followLerp >= 1
      ? 1
      : 1 - Math.pow(1 - this.followLerp, dt * 60)

    this.currentX += (this.targetX - this.currentX) * lerpFactor
    this.currentZ += (this.targetZ - this.currentZ) * lerpFactor
    this.zoom += (this.targetZoom - this.zoom) * lerpFactor * 0.5

    // Update shake offset with linear decay and pixel-snapping
    if (this.shakeElapsed < this.shakeDuration) {
      this.shakeElapsed += dt
      const progress = this.shakeElapsed / this.shakeDuration
      const decay = 1 - progress
      const amplitude = this.shakeIntensity * decay
      this.shakeOffsetX = Math.round((Math.random() * 2 - 1) * amplitude)
      this.shakeOffsetZ = Math.round((Math.random() * 2 - 1) * amplitude)
    } else {
      this.shakeOffsetX = 0
      this.shakeOffsetZ = 0
    }

    // Apply to container (including shake offset)
    this.container.scale.set(this.zoom)
    this.container.position.set(
      this.screenWidth / 2 - this.currentX * this.zoom + this.shakeOffsetX,
      this.screenHeight / 2 - this.currentZ * this.zoom + this.shakeOffsetZ
    )
  }

  snapToTarget(): void {
    this.updateScreenSize()
    this.currentX = this.targetX
    this.currentZ = this.targetZ
    this.zoom = this.targetZoom
    this.container.scale.set(this.zoom)
    this.container.position.set(
      this.screenWidth / 2 - this.currentX * this.zoom + this.shakeOffsetX,
      this.screenHeight / 2 - this.currentZ * this.zoom + this.shakeOffsetZ,
    )
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  screenToWorld(screenX: number, screenY: number): { x: number; z: number } {
    return {
      x: (screenX - this.screenWidth / 2) / this.zoom + this.currentX,
      z: (screenY - this.screenHeight / 2) / this.zoom + this.currentZ,
    }
  }

  /**
   * Get the visible world bounds (for viewport culling)
   */
  getViewport(): { x: number; z: number; width: number; height: number } {
    const margin = 1.2
    const w = (this.screenWidth / this.zoom) * margin
    const h = (this.screenHeight / this.zoom) * margin
    return {
      x: this.currentX - w / 2,
      z: this.currentZ - h / 2,
      width: w,
      height: h,
    }
  }

  getZoom(): number {
    return this.zoom
  }

  getPosition(): { x: number; z: number } {
    return { x: this.currentX, z: this.currentZ }
  }
}
